"use client";

import {
  addDays,
  differenceInMinutes,
  format,
  isSaturday,
  isSunday,
  isToday,
  isTomorrow,
  nextSaturday,
  nextSunday,
} from "date-fns";
import {
  Handshake,
  type LucideIcon,
  MapPin,
  Minus,
  Plus,
  Shirt,
  Trophy,
  User,
  Users,
  UsersRound,
} from "lucide-react";
import { type ReactNode, useState } from "react";
import { AudienceSelector } from "@/components/audience-selector";
import { LocationSearchBox } from "@/components/location-search-box";
import { RichPostComposer } from "@/components/posts/rich-post-composer";
import { DateTimeField } from "@/components/ui/date-time-field";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import type { ActivitySessionFormat } from "@/db/schema/activities";
import type { ActivityDefinition } from "@/lib/activities";
import {
  getActivitySessionRules,
  getDefaultSessionFormat,
  getSessionParticipantRange,
} from "@/lib/activity-config";
import type { SimpleFormApi } from "@/lib/form";
import { uiHaptics } from "@/lib/ui-haptics";
import { cn } from "@/lib/utils";
import type { ActivitySessionValues } from "@/validations/activity-session";
import { ActivityGlyph } from "./activity-glyph";
import {
  SKILL_LEVEL_MAX,
  SKILL_LEVEL_MIN,
} from "./activity-session-drawer.constants";
import {
  addMinutesToTimeString,
  buildGeneratedSessionTitle,
  getActivitySkillLabel,
  getParticipantRangeLabel,
  getSessionFormatLabel,
  supportsSkillRequirements,
} from "./activity-session-drawer.utils";
import { SessionBookingSelector } from "./session-booking-selector.client";
import { SessionSplitCreationFields } from "./session-split-creation-fields.client";

const FORMAT_ICONS: Record<ActivitySessionFormat, LucideIcon> = {
  singles: User,
  doubles: Users,
  team: Shirt,
  group: UsersRound,
};

const CARD =
  "rounded-2xl border border-border bg-background dark:border-white/10 dark:bg-white/[0.04]";

// Native date/time inputs keep the OS wheel picker on phones; 16px stops iOS
// from zooming the sheet when one opens.
const TIME_INPUT =
  "h-10 w-[10.5rem] appearance-none rounded-full border border-black/15 bg-transparent px-3.5 text-base text-text-primary tabular-nums outline-none [-webkit-appearance:none] focus-visible:ring-2 focus-visible:ring-brand/40 dark:border-white/10 [&::-webkit-date-and-time-value]:m-0 [&::-webkit-date-and-time-value]:text-left";

// Date and times share one column width so the three controls line up.
const PICKER_WIDTH = "w-[10.5rem]";

function OptionalTag() {
  return (
    <span className="inline-flex h-5 shrink-0 items-center rounded-full bg-text-primary/8 px-2 text-[10px] font-semibold tracking-wider text-text-secondary uppercase">
      Optional
    </span>
  );
}

/**
 * One titled block of the form. `bare` drops the card for children that bring
 * their own (the split fields).
 */
function Group({
  title,
  optional = false,
  footnote,
  bare = false,
  children,
}: {
  title: string;
  optional?: boolean;
  footnote?: ReactNode;
  bare?: boolean;
  children: ReactNode;
}) {
  return (
    <section className="space-y-2">
      <div className="flex items-center gap-2 px-1">
        <h3 className="text-[13px] font-semibold text-text-secondary">
          {title}
        </h3>
        {optional ? <OptionalTag /> : null}
      </div>
      {bare ? (
        children
      ) : (
        <div
          className={cn(
            CARD,
            "divide-y divide-border overflow-hidden dark:divide-white/10",
          )}
        >
          {children}
        </div>
      )}
      {footnote ? (
        <p className="px-1 text-xs leading-5 text-text-tertiary">{footnote}</p>
      ) : null}
    </section>
  );
}

function Row({
  label,
  htmlFor,
  description,
  children,
}: {
  label: string;
  htmlFor?: string;
  description?: string;
  children: ReactNode;
}) {
  return (
    <div className="flex min-h-[3.25rem] items-center justify-between gap-3 px-4 py-2.5">
      <div className="min-w-0">
        <label
          htmlFor={htmlFor}
          className="block text-[15px] font-medium text-text-primary"
        >
          {label}
        </label>
        {description ? (
          <p className="mt-0.5 text-xs leading-4 text-text-tertiary">
            {description}
          </p>
        ) : null}
      </div>
      <div className="shrink-0">{children}</div>
    </div>
  );
}

function OptionCard({
  selected,
  onClick,
  icon: Icon,
  title,
  detail,
}: {
  selected: boolean;
  onClick: () => void;
  icon: LucideIcon;
  title: string;
  detail?: string;
}) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onClick}
      className={cn(
        "flex min-w-0 flex-col items-start gap-2 rounded-xl border p-3 text-left transition-[border-color,background-color,transform] duration-150 ease-out-strong active:scale-[0.98]",
        selected
          ? "border-brand bg-brand/[0.06] dark:bg-brand/15"
          : "border-border bg-surface dark:border-white/10 dark:bg-transparent pointer-fine:hover:border-border-strong",
      )}
    >
      <Icon
        className={cn(
          "size-[18px]",
          selected ? "text-brand" : "text-text-secondary",
        )}
      />
      <span className="block min-w-0">
        <span
          className={cn(
            "block text-sm font-semibold",
            selected ? "text-brand" : "text-text-primary",
          )}
        >
          {title}
        </span>
        {detail ? (
          <span className="mt-0.5 block text-xs leading-4 text-text-tertiary">
            {detail}
          </span>
        ) : null}
      </span>
    </button>
  );
}

function Stepper({
  label,
  value,
  min,
  max,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  onChange: (next: number) => void;
}) {
  const [draft, setDraft] = useState(String(value));
  const [lastValue, setLastValue] = useState(value);
  if (value !== lastValue) {
    setLastValue(value);
    setDraft(String(value));
  }
  const clamp = (n: number) => Math.min(max, Math.max(min, n));
  const commitDraft = () => {
    const parsed = Number.parseInt(draft, 10);
    const next = Number.isFinite(parsed) ? clamp(parsed) : value;
    setDraft(String(next));
    if (next !== value) onChange(next);
  };
  const step = (delta: number) => {
    const next = clamp(value + delta);
    if (next === value) return;
    uiHaptics.select();
    onChange(next);
  };
  const buttonClass =
    "flex size-9 items-center justify-center rounded-full bg-surface-alt text-text-primary transition-[transform,opacity] duration-100 ease-out-strong active:scale-90 disabled:opacity-35 dark:bg-white/[0.08]";

  return (
    <div className="flex items-center gap-1.5">
      <button
        type="button"
        onClick={() => step(-1)}
        disabled={value <= min}
        aria-label={`Fewer ${label.toLowerCase()}`}
        className={buttonClass}
      >
        <Minus className="size-4" />
      </button>
      {/* Typeable too: a ±1 stepper alone is 180 taps from 200 down to 20. */}
      <input
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        enterKeyHint="done"
        aria-label={label}
        value={draft}
        onChange={(e) => setDraft(e.target.value.replace(/\D/g, ""))}
        onFocus={(e) => e.target.select()}
        onBlur={commitDraft}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            e.currentTarget.blur();
          }
        }}
        className="h-9 w-12 rounded-lg bg-transparent text-center text-base font-semibold text-text-primary tabular-nums outline-none focus:bg-surface-alt focus-visible:ring-2 focus-visible:ring-brand/40 dark:focus:bg-white/[0.08]"
      />
      <button
        type="button"
        onClick={() => step(1)}
        disabled={value >= max}
        aria-label={`More ${label.toLowerCase()}`}
        className={buttonClass}
      >
        <Plus className="size-4" />
      </button>
    </div>
  );
}

const toDateKey = (date: Date) => format(date, "yyyy-MM-dd");

/** Today, tomorrow, and the coming weekend — the days people actually pick. */
function getQuickDays(now: Date) {
  const days = [
    { key: toDateKey(now), label: "Today" },
    { key: toDateKey(addDays(now, 1)), label: "Tomorrow" },
  ];
  for (const day of [
    isSaturday(now) ? now : nextSaturday(now),
    isSunday(now) ? now : nextSunday(now),
  ]) {
    const key = toDateKey(day);
    if (!days.some((existing) => existing.key === key)) {
      days.push({ key, label: format(day, "EEE d") });
    }
  }
  return days;
}

function describeWhen(startDate: string, startTime: string, endTime: string) {
  const start = new Date(`${startDate}T${startTime}`);
  const endSameDay = new Date(`${startDate}T${endTime}`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(endSameDay.getTime())) {
    return "Pick a time";
  }
  const end = endSameDay > start ? endSameDay : addDays(endSameDay, 1);
  const day = isToday(start)
    ? "Today"
    : isTomorrow(start)
      ? "Tomorrow"
      : format(start, "EEE d MMM");
  const minutes = differenceInMinutes(end, start);
  const hours = minutes / 60;
  const duration =
    minutes < 60
      ? `${minutes} min`
      : Number.isInteger(hours)
        ? `${hours} h`
        : `${hours.toFixed(1)} h`;
  return `${day} · ${format(start, "h:mm a")} – ${format(end, "h:mm a")} · ${duration}`;
}

type EventDetailsStepProps = {
  form: SimpleFormApi<ActivitySessionValues>;
  activities: ActivityDefinition[];
  currentUser: {
    displayName: string;
    avatarUrl?: string | null;
  };
  endTimeWasManual: React.MutableRefObject<boolean>;
};

export function EventDetailsStep({
  form,
  activities,
  currentUser,
  endTimeWasManual,
}: EventDetailsStepProps) {
  const quickDays = getQuickDays(new Date());

  return (
    <form
      id="activity-session-form"
      className="space-y-6"
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        void form.handleSubmit();
      }}
    >
      <form.Subscribe
        selector={(state) => ({
          activityId: state.values.activityId,
          startDate: state.values.startDate,
          startTime: state.values.startTime,
          endTime: state.values.endTime,
        })}
      >
        {({ activityId, startDate, startTime, endTime }) => {
          const selectedActivity = activities.find((a) => a.id === activityId);
          if (!selectedActivity) return null;

          return (
            <div className="flex items-center gap-3.5">
              <ActivityGlyph activity={selectedActivity} size="lg" />
              <div className="min-w-0">
                <p className="truncate text-lg leading-6 font-semibold text-text-primary">
                  {buildGeneratedSessionTitle({
                    activityName: selectedActivity.name,
                    displayName: currentUser.displayName,
                  })}
                </p>
                <p className="mt-0.5 truncate text-sm text-text-secondary tabular-nums">
                  {describeWhen(startDate, startTime, endTime)}
                </p>
              </div>
            </div>
          );
        }}
      </form.Subscribe>

      <Group title="When">
        <form.Field name="startDate">
          {(field) => (
            <>
              <div className="no-scrollbar flex gap-1.5 overflow-x-auto px-3 pt-3 pb-2.5">
                {quickDays.map((day) => {
                  const isActive = field.state.value === day.key;
                  return (
                    <button
                      key={day.key}
                      type="button"
                      aria-pressed={isActive}
                      onClick={() => field.handleChange(day.key)}
                      className={cn(
                        "h-8 shrink-0 rounded-full px-3.5 text-[13px] font-semibold transition-colors duration-150 ease-out-strong",
                        isActive
                          ? "bg-brand text-white"
                          : "bg-surface-alt text-text-secondary dark:bg-white/[0.08] pointer-fine:hover:text-text-primary",
                      )}
                    >
                      {day.label}
                    </button>
                  );
                })}
              </div>
              <Row label="Date" htmlFor="date">
                <DateTimeField
                  id="date"
                  value={field.state.value}
                  onChange={field.handleChange}
                  min={format(new Date(), "yyyy-MM-dd")}
                  className={PICKER_WIDTH}
                />
              </Row>
            </>
          )}
        </form.Field>

        <form.Field name="startTime">
          {(field) => (
            <Row label="Starts" htmlFor="startTime">
              <input
                id="startTime"
                type="time"
                value={field.state.value}
                onChange={(e) => {
                  const nextStartTime = e.target.value;
                  field.handleChange(nextStartTime);
                  if (!endTimeWasManual.current) {
                    form.setFieldValue(
                      "endTime",
                      addMinutesToTimeString(nextStartTime, 60),
                    );
                  }
                }}
                className={TIME_INPUT}
              />
            </Row>
          )}
        </form.Field>
        <form.Field name="endTime">
          {(field) => (
            <Row label="Ends" htmlFor="endTime">
              <input
                id="endTime"
                type="time"
                value={field.state.value}
                onChange={(e) => {
                  endTimeWasManual.current = true;
                  field.handleChange(e.target.value);
                }}
                className={TIME_INPUT}
              />
            </Row>
          )}
        </form.Field>
      </Group>

      <Group title="Where">
        <div className="space-y-3 p-3">
          <form.Subscribe
            selector={(state) => ({
              latitude: state.values.latitude,
              longitude: state.values.longitude,
            })}
          >
            {({ latitude, longitude }) => (
              <LocationSearchBox
                onSelect={(place) => {
                  const address = place.display_name;
                  const locationName = address.split(",")[0]?.trim() || address;
                  form.setFieldValue("locationName", locationName);
                  form.setFieldValue("address", address);
                  form.setFieldValue("latitude", Number.parseFloat(place.lat));
                  form.setFieldValue("longitude", Number.parseFloat(place.lon));
                }}
                placeholder="Court, park or address"
                className="w-full"
                inputClassName="h-11 rounded-xl border-transparent bg-surface-alt text-base font-normal shadow-none backdrop-blur-none placeholder:text-text-tertiary focus-visible:scale-100 md:text-base dark:border-transparent dark:bg-white/[0.07] dark:shadow-none"
                searchProfile="mixed"
                biasCenter={
                  Number.isFinite(latitude) &&
                  Number.isFinite(longitude) &&
                  (latitude !== 0 || longitude !== 0)
                    ? { latitude, longitude }
                    : undefined
                }
              />
            )}
          </form.Subscribe>
          <form.Subscribe
            selector={(state) => ({
              locationName: state.values.locationName,
              address: state.values.address,
            })}
          >
            {({ locationName, address }) =>
              address ? (
                <div className="flex items-start gap-2.5 px-1">
                  <MapPin className="mt-0.5 size-4 shrink-0 text-brand" />
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-text-primary">
                      {locationName}
                    </p>
                    <p className="line-clamp-2 text-xs leading-4 text-text-tertiary">
                      {address}
                    </p>
                  </div>
                </div>
              ) : null
            }
          </form.Subscribe>
        </div>
        <form.Field name="venueBookingId">
          {(field) => (
            <SessionBookingSelector
              value={field.state.value}
              onChange={field.handleChange}
            />
          )}
        </form.Field>
      </Group>

      <form.Subscribe
        selector={(state) => ({
          activityId: state.values.activityId,
          sessionFormat: state.values.sessionFormat,
        })}
      >
        {({ activityId, sessionFormat }) => {
          if (!activityId) return null;
          const selectedActivity = activities.find((a) => a.id === activityId);
          const rules = getActivitySessionRules(selectedActivity);
          const resolvedFormat =
            sessionFormat ?? getDefaultSessionFormat(selectedActivity);
          const showCompetitionMode =
            Boolean(rules?.competition.allowed) &&
            Boolean(rules?.result.allowed);
          const formats = rules?.session.formats ?? [];
          const showFormatSelector = formats.length > 1;

          if (!showCompetitionMode && !showFormatSelector) {
            return null;
          }

          return (
            <Group title="Format">
              {showFormatSelector ? (
                <form.Field name="sessionFormat">
                  {(field) => (
                    <div
                      className={cn(
                        "grid gap-2 p-3",
                        formats.length === 2 ? "grid-cols-2" : "grid-cols-3",
                      )}
                    >
                      {formats.map((fmt) => {
                        const range = rules?.session.participantRange[fmt];
                        return (
                          <OptionCard
                            key={fmt}
                            selected={resolvedFormat === fmt}
                            onClick={() => {
                              field.handleChange(fmt);
                              const nextRange = getSessionParticipantRange(
                                selectedActivity,
                                fmt,
                              );
                              form.setFieldValue(
                                "minParticipants",
                                nextRange.min,
                              );
                              form.setFieldValue(
                                "maxParticipants",
                                nextRange.max,
                              );
                              if (
                                !supportsSkillRequirements(
                                  selectedActivity,
                                  fmt,
                                )
                              ) {
                                form.setFieldValue("minSkillLevel", null);
                                form.setFieldValue("maxSkillLevel", null);
                              }
                            }}
                            icon={FORMAT_ICONS[fmt] ?? Users}
                            title={getSessionFormatLabel(fmt)}
                            detail={
                              range
                                ? getParticipantRangeLabel(range.min, range.max)
                                : undefined
                            }
                          />
                        );
                      })}
                    </div>
                  )}
                </form.Field>
              ) : null}

              {showCompetitionMode ? (
                <form.Field name="competitionMode">
                  {(field) => (
                    <div className="grid grid-cols-2 gap-2 p-3">
                      <OptionCard
                        selected={field.state.value === "casual"}
                        onClick={() => field.handleChange("casual")}
                        icon={Handshake}
                        title="Casual"
                        detail="Score optional, no ranking"
                      />
                      <OptionCard
                        selected={field.state.value === "competitive"}
                        onClick={() => field.handleChange("competitive")}
                        icon={Trophy}
                        title="Competitive"
                        detail="Verified scores move rankings"
                      />
                    </div>
                  )}
                </form.Field>
              ) : null}
            </Group>
          );
        }}
      </form.Subscribe>

      <form.Subscribe
        selector={(state) => ({
          activityId: state.values.activityId,
          sessionFormat: state.values.sessionFormat,
          minParticipants: state.values.minParticipants,
          maxParticipants: state.values.maxParticipants,
        })}
      >
        {({ activityId, sessionFormat, minParticipants, maxParticipants }) => {
          const selectedActivity = activities.find(
            (activity) => activity.id === activityId,
          );
          const resolvedFormat =
            sessionFormat ?? getDefaultSessionFormat(selectedActivity);
          const range = getSessionParticipantRange(
            selectedActivity,
            resolvedFormat,
          );

          if (range.min === range.max) return null;

          return (
            <Group
              title="People"
              footnote={`${getSessionFormatLabel(resolvedFormat)} fits ${getParticipantRangeLabel(range.min, range.max)}.`}
            >
              <Row label="Minimum" description="Needed for it to happen">
                <Stepper
                  label="Minimum"
                  value={minParticipants}
                  min={range.min}
                  max={Math.min(range.max, maxParticipants)}
                  onChange={(next) =>
                    form.setFieldValue("minParticipants", next)
                  }
                />
              </Row>
              <Row label="Capacity" description="Spots including you">
                <Stepper
                  label="Capacity"
                  value={maxParticipants}
                  min={Math.max(range.min, minParticipants)}
                  max={range.max}
                  onChange={(next) =>
                    form.setFieldValue("maxParticipants", next)
                  }
                />
              </Row>
            </Group>
          );
        }}
      </form.Subscribe>

      <form.Subscribe
        selector={(state) => ({
          activityId: state.values.activityId,
          sessionFormat: state.values.sessionFormat,
          minSkillLevel: state.values.minSkillLevel,
          maxSkillLevel: state.values.maxSkillLevel,
        })}
      >
        {({ activityId, sessionFormat, minSkillLevel, maxSkillLevel }) => {
          if (!activityId) return null;

          const selectedActivity = activities.find(
            (activity) => activity.id === activityId,
          );
          const resolvedFormat =
            sessionFormat ?? getDefaultSessionFormat(selectedActivity);

          if (!supportsSkillRequirements(selectedActivity, resolvedFormat)) {
            return null;
          }

          const isSkillRangeEnabled =
            minSkillLevel != null && maxSkillLevel != null;
          const skillRange = [
            minSkillLevel ?? SKILL_LEVEL_MIN,
            maxSkillLevel ?? SKILL_LEVEL_MAX,
          ] as const;

          return (
            <Group title="Skill level" optional>
              <Row
                label="Limit by level"
                htmlFor="skill-range-enabled"
                description="Only players in your band can join"
              >
                <Switch
                  id="skill-range-enabled"
                  checked={isSkillRangeEnabled}
                  onCheckedChange={(checked) => {
                    if (!checked) {
                      form.setFieldValue("minSkillLevel", null);
                      form.setFieldValue("maxSkillLevel", null);
                      return;
                    }
                    form.setFieldValue(
                      "minSkillLevel",
                      minSkillLevel ?? SKILL_LEVEL_MIN,
                    );
                    form.setFieldValue(
                      "maxSkillLevel",
                      maxSkillLevel ?? SKILL_LEVEL_MAX,
                    );
                  }}
                />
              </Row>

              {isSkillRangeEnabled ? (
                <div className="space-y-4 px-4 pt-3 pb-4">
                  <p className="text-center text-sm font-semibold text-text-primary">
                    {skillRange[0] === skillRange[1]
                      ? getActivitySkillLabel(selectedActivity, skillRange[0])
                      : `${getActivitySkillLabel(selectedActivity, skillRange[0])} – ${getActivitySkillLabel(selectedActivity, skillRange[1])}`}
                  </p>
                  <Slider
                    value={[skillRange[0], skillRange[1]]}
                    min={SKILL_LEVEL_MIN}
                    max={SKILL_LEVEL_MAX}
                    step={1}
                    minStepsBetweenThumbs={0}
                    onValueChange={(value) => {
                      const nextMin = Math.min(
                        value[0] ?? SKILL_LEVEL_MIN,
                        value[1] ?? SKILL_LEVEL_MAX,
                      );
                      const nextMax = Math.max(
                        value[0] ?? SKILL_LEVEL_MIN,
                        value[1] ?? SKILL_LEVEL_MAX,
                      );
                      form.setFieldValue("minSkillLevel", nextMin);
                      form.setFieldValue("maxSkillLevel", nextMax);
                    }}
                  />
                  <div className="flex items-center justify-between text-[11px] text-text-tertiary tabular-nums">
                    {Array.from(
                      { length: SKILL_LEVEL_MAX - SKILL_LEVEL_MIN + 1 },
                      (_, index) => SKILL_LEVEL_MIN + index,
                    ).map((level) => (
                      <span key={level}>{level}</span>
                    ))}
                  </div>
                </div>
              ) : null}
            </Group>
          );
        }}
      </form.Subscribe>

      <Group title="Who can join">
        <div className="p-3">
          <form.Field name="visibility">
            {(field) => (
              <AudienceSelector
                value={field.state.value}
                onChange={(next) => field.handleChange(next)}
                options={[
                  {
                    value: "buddies",
                    label: "Buddies only",
                    description: "Only your buddies can find this session.",
                  },
                  { value: "close_friends" },
                  { value: "public" },
                  {
                    value: "private",
                    label: "Invite only",
                    description: "Only people you invite can join.",
                  },
                ]}
                variant="wide"
              />
            )}
          </form.Field>
        </div>
        <form.Field name="requiresApproval">
          {(field) => (
            <Row
              label="Approve each player"
              htmlFor="requiresApproval"
              description="People request, you say yes"
            >
              <Switch
                id="requiresApproval"
                checked={field.state.value}
                onCheckedChange={(checked) =>
                  field.handleChange(Boolean(checked))
                }
              />
            </Row>
          )}
        </form.Field>
      </Group>

      <Group title="Money" optional bare>
        <SessionSplitCreationFields form={form} />
      </Group>

      <Group title="Note" optional>
        <div className="p-3">
          <form.Field name="description">
            {(field) => (
              <RichPostComposer
                currentUser={currentUser}
                value={field.state.value}
                onChange={field.handleChange}
                placeholder="What should people know? Gear, parking, the plan…"
                rows={4}
                textareaContainerClassName="min-h-24"
                textareaClassName="min-h-24"
              />
            )}
          </form.Field>
        </div>
      </Group>
    </form>
  );
}
